import {
  Add,
  ArrowBack,
  ArrowForward,
  Clear,
  ContentCopy,
  Delete,
} from '@mui/icons-material';
import { Box, Collapse, Paper, Stack } from '@mui/material';
import { ROUTES } from 'config';
import {
  ActionsMenu,
  Button,
  DataLossPrompt,
  FileInput,
  Footer,
  ImageList,
  LanguagesInput,
  SelectInput,
  SortableList,
  SwitchInput,
  TextInput,
  Typography,
  VideoInput,
} from 'melp-design/components';
import { SystemColors } from 'melp-design/style';
import moment from 'moment';
import { orderBy } from 'lodash';
import { FC, useEffect, useMemo, useState } from 'react';
import {
  Controller,
  SubmitHandler,
  useFieldArray,
  useForm,
  SubmitErrorHandler,
} from 'react-hook-form-latest';
import { useTranslation } from 'react-i18next';
import { useCompanyLanguages, useMe } from 'state/Administrators';
import {
  getSurveyPermissionName,
  surveyAnswerTypes,
  surveyChoiceAnswerFormats,
  surveyChoiceAnswerTypes,
  surveyRatingScaleAnswerTypes,
  useActivateSurvey,
  useSurvey,
  useUpdateSurveySettingsAndContent,
} from 'store/surveys';
import { includes } from 'utils/general';
import { usePredefinedToasts } from 'utils/Toast';
import { useRouteParams } from 'utils/useIdParam';
import { useUploadFile } from 'store/files';
import { toast } from 'react-toastify';
import { Values } from './survey-content.types';
import {
  createEmptyQuestion,
  getInitialValues,
} from './survey-content.constants';
import {
  AnswersList,
  AttachmentsList,
  QuestionsList,
  QuestionTranslationsList,
} from './atoms';

interface Props {
  onSaveSuccess: () => void;
}

export const SurveyContent: FC<Props> = ({ onSaveSuccess }) => {
  const { t } = useTranslation();
  const { id: surveyId } = useRouteParams(ROUTES.surveys.survey.details);
  const predefinedToasts = usePredefinedToasts();
  const { defaultLanguage, supportedLanguages } = useCompanyLanguages();
  const { isFeatureEnabled, hasEditPermission } = useMe();
  const { data: survey } = useSurvey(surveyId);

  const INITIAL_VALUES = useMemo(
    () => getInitialValues(defaultLanguage, survey?.type),
    [defaultLanguage, survey?.type],
  );
  const emptyQuestion = useMemo(
    () => createEmptyQuestion(defaultLanguage),
    [defaultLanguage],
  );
  const [isFormReady, setIsFormReady] = useState(false);

  const { mutate: updateSurveySettingsAndContent, isLoading: isUpdating } =
    useUpdateSurveySettingsAndContent();
  const { mutate: activateSurvey, isLoading: isActivating } =
    useActivateSurvey();
  const { mutate: uploadFile, isLoading: isUploading } = useUploadFile();

  const {
    control,
    handleSubmit,
    getValues,
    setValue,
    trigger,
    reset,
    watch,
    formState: {
      dirtyFields: { ui: _ui, ...dirtyFields },
    },
  } = useForm<Values>({
    defaultValues: INITIAL_VALUES,
  });
  const { fields: translationsFields, replace: replaceTranslations } =
    useFieldArray({ name: 'translations', control });
  const {
    fields: questionFields,
    replace: replaceQuestions,
    append: appendQuestion,
    move: moveQuestions,
    remove: removeQuestion,
    update: updateQuestion,
  } = useFieldArray({
    name: 'questions',
    control,
  });

  useEffect(() => {
    if (!survey) return;

    const initialValues = getInitialValues(defaultLanguage, survey.type);

    if (!survey.translations.length) {
      reset(initialValues);
      setIsFormReady(true);
      return;
    }

    reset({
      ...initialValues,
      settings: {
        withDescription: survey.metadata.withDescription,
        withImage: survey.metadata.withImage,
        withAttachments: survey.metadata.withAttachments,
        withVideo: survey.metadata.withVideo,
      },
      ui: {
        ...initialValues.ui,
      },
      languages: orderBy(
        survey.translations.map((trans) => trans.language),
        [(language) => language === defaultLanguage],
        ['desc'],
      ),
      translations: survey.translations.map((trans) => ({
        language: trans.language,
        title: trans.title,
        description: trans.description,
        imageId: trans.imageId ?? '',
        attachments: trans.attachments.map((attachment) => ({
          fileId: attachment.id,
          name: attachment.url,
        })),
        videos: trans.videos,
      })),
      questions: survey.questions.map((question) => ({
        type: includes(surveyRatingScaleAnswerTypes, question.type)
          ? 'rating_scale'
          : question.type,
        isRequired: question.isRequired,
        subType: includes(surveyRatingScaleAnswerTypes, question.type)
          ? question.type
          : ('' as const),
        choiceAnswerFormat: question.metadata.choiceAnswerFormat,
        conditionalJump: {
          isEnabled: question.metadata.conditionalJumpRules.length > 0,
          rules: question.metadata.conditionalJumpRules,
        },
        answers: question.options.map((option) => ({
          imageId: option.translations[0].imageId || null,
          isCorrect: option.translations[0].isCorrect,
          translations: option.translations.map((trans) => ({
            language: trans.language,
            text: trans.text,
          })),
        })),
        withImage: question.metadata.withImage,
        withDescription: question.metadata.withDescription,
        withAttachments: question.metadata.withAttachments,
        withVideo: question.metadata.withVideo,
        imageId: question.translations[0].imageId || null,
        translations: question.translations.map((trans) => ({
          language: trans.language,
          text: trans.text,
          description: trans.description,
          attachments: trans.attachments.map((attachment) => ({
            fileId: attachment.id,
            name: attachment.url,
          })),
          videos: trans.videos,
        })),
      })),
    });
    setIsFormReady(true);
  }, [defaultLanguage, reset, survey]);

  if (!survey || !isFormReady) return null;

  const hasSurveyEditPermission = survey
    ? hasEditPermission([getSurveyPermissionName(survey.type)])
    : false;
  const canEdit = hasSurveyEditPermission;

  const onSubmit: SubmitHandler<Values> = (values) => {
    const hasRequiredBetweenJumps = values.questions.some((question, index) =>
      question.conditionalJump.rules.some((rule) => {
        const [start, end] = [
          Math.min(index, rule.questionIndex),
          Math.max(index, rule.questionIndex),
        ];
        return values.questions.slice(start + 1, end).some((q) => q.isRequired);
      }),
    );

    if (hasRequiredBetweenJumps) {
      toast.error(t('surveys.conditional_jump.required_between_jumps_error'));
      return;
    }

    const hasQuestionPage = values.questions.some(
      (question) => question.type !== 'information_page',
    );
    const turnScoringOff =
      survey.type === 'training' && !hasQuestionPage && survey.scoringEnabled;

    updateSurveySettingsAndContent(
      {
        surveyId,
        settings: {
          metadata: {
            withDescription: values.settings.withDescription,
            withImage: values.settings.withImage,
            withAttachments: values.settings.withAttachments,
            withVideo: values.settings.withVideo,
          },
          ...(turnScoringOff ? { scoringEnabled: false } : {}),
        },
        content: {
          translations: values.translations.map(
            ({ imageId, description, attachments, videos, ...trans }) => ({
              ...trans,
              description: values.settings.withDescription ? description : '',
              imageId: values.settings.withImage ? imageId || null : null,
              attachmentIds: values.settings.withAttachments
                ? attachments.map((attachment) => attachment.fileId)
                : [],
              videoUrls: values.settings.withVideo ? videos : [],
            }),
          ),
          questions: values.questions.map((question) => ({
            type: includes(['rating_scale'], question.type)
              ? question.subType || surveyRatingScaleAnswerTypes[0]
              : question.type,
            isRequired: question.isRequired,
            translations: question.translations.map(
              ({ attachments, description, videos, ...trans }) => ({
                ...trans,
                description: question.withDescription ? description : '',
                attachmentIds: question.withAttachments
                  ? attachments.map((attachment) => attachment.fileId)
                  : [],
                videoUrls: question.withVideo ? videos : [],
                imageId: question.withImage ? question.imageId || null : null,
              }),
            ),
            answers: question.answers.map(
              ({ imageId, isCorrect, ...answer }) => ({
                ...answer,
                translations: answer.translations.map((trans) => ({
                  language: trans.language,
                  isCorrect,
                  ...(question.choiceAnswerFormat === 'text'
                    ? { text: trans.text, imageId: null }
                    : question.choiceAnswerFormat === 'image'
                    ? { text: '', imageId }
                    : { text: trans.text, imageId }),
                })),
              }),
            ),
            metadata: {
              withImage: question.withImage,
              withDescription: question.withDescription,
              withAttachments: question.withAttachments,
              withVideo: question.withVideo,
              choiceAnswerFormat: question.choiceAnswerFormat,
              conditionalJumpRules: question.conditionalJump.rules,
            },
          })),
        },
      },
      {
        onSuccess: () => {
          reset(values, { keepDirty: false });
          predefinedToasts.success.updated();
          onSaveSuccess();
        },
      },
    );
  };

  const isDirty = !!Object.keys(dirtyFields).length;
  const [languages, translations, questions, settings, ui] = watch([
    'languages',
    'translations',
    'questions',
    'settings',
    'ui',
  ]);

  const hasConditionalJumpRules = questions.some(
    (q) => q.conditionalJump.isEnabled,
  );

  const resetConditionalJumps = () => {
    questions.forEach((_, index) => {
      setValue(
        `questions.${index}.conditionalJump`,
        emptyQuestion.conditionalJump,
      );
    });
  };

  const onError: SubmitErrorHandler<Values> = (errors) => {
    const firstInvalidContentLanguage = languages.find(
      (_, index) => !!errors.translations?.[index],
    );

    if (firstInvalidContentLanguage) {
      setValue('ui.selectedLanguage', firstInvalidContentLanguage);
      setValue('ui.selectedQuestionIndex', NaN);
      return;
    }

    const firstInvalidQuestionIndex = getValues('questions').findIndex(
      (_, index) => !!errors.questions?.[index],
    );

    if (firstInvalidQuestionIndex < 0) return;

    const getInvalidQuestionInvalidLanguages = () => {
      const questionError = errors.questions?.[firstInvalidQuestionIndex];

      if (!questionError) return [];

      const hasError = (value: unknown) =>
        !!value && typeof value === 'object' && !!Object.keys(value).length;

      return languages.filter((_, index) => {
        const hasQuestionTranslationError = hasError(
          questionError.translations?.[index],
        );
        if (hasQuestionTranslationError) return true;

        if (!Array.isArray(questionError.answers)) return false;

        return questionError.answers.some((answerError) =>
          hasError(answerError?.translations?.[index]),
        );
      });
    };

    const firstInvalidQuestionLanguage =
      getInvalidQuestionInvalidLanguages()[0];

    if (firstInvalidQuestionLanguage !== undefined) {
      setValue('ui.selectedLanguage', firstInvalidQuestionLanguage);
      setValue('ui.selectedQuestionIndex', firstInvalidQuestionIndex);
      return;
    }

    // Fallback for non-language-specific answer errors (e.g. imageId).
    setValue('ui.selectedQuestionIndex', firstInvalidQuestionIndex);
  };

  return (
    <>
      <form onSubmit={handleSubmit(onSubmit, onError)}>
        <Stack gap={1}>
          <Paper variant="outlined">
            <Stack sx={{ p: 1 }}>
              <LanguagesInput
                availableLanguages={supportedLanguages}
                disabledLanguages={[defaultLanguage]}
                selectedLanguages={languages}
                selectedLanguage={ui.selectedLanguage}
                disabled={!canEdit}
                onSelectedChange={(value) =>
                  setValue('ui.selectedLanguage', value)
                }
                onLanguagesChange={(value) => {
                  replaceTranslations(
                    value.map(
                      (language) =>
                        getValues('translations').find(
                          (t) => t.language === language,
                        ) ?? {
                          ...INITIAL_VALUES.translations[0],
                          language,
                        },
                    ),
                  );
                  replaceQuestions(
                    questions.map((question) => ({
                      ...question,
                      answers: question.answers.map((answer) => ({
                        ...answer,
                        translations: value.map(
                          (language) =>
                            answer.translations.find(
                              (t) => t.language === language,
                            ) ?? {
                              ...emptyQuestion.answers[0].translations[0],
                              language,
                            },
                        ),
                      })),
                      translations: value.map(
                        (language) =>
                          question.translations.find(
                            (t) => t.language === language,
                          ) ?? {
                            ...emptyQuestion.translations[0],
                            language,
                          },
                      ),
                    })),
                  );

                  if (!value.includes(ui.selectedLanguage)) {
                    setValue('ui.selectedLanguage', defaultLanguage);
                  }
                  setValue('languages', value);
                }}
              />
            </Stack>
          </Paper>

          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              gap: 1,
              minWidth: 0,
            }}
          >
            <Box sx={{ width: 250, flexShrink: 0 }}>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Stack gap={2}>
                  <Typography variant="h3">
                    {t('surveys.content.pages')}
                  </Typography>

                  <Stack
                    direction="row"
                    px={2}
                    py={1.5}
                    sx={{
                      cursor: 'pointer',
                      backgroundColor: Number.isNaN(ui.selectedQuestionIndex)
                        ? SystemColors.primary.tint
                        : SystemColors.white,
                    }}
                    borderRadius={1}
                    boxShadow={` 0 0 0 1px ${SystemColors.grey[90]}`}
                    onClick={() => {
                      setValue('ui.selectedQuestionIndex', NaN);
                    }}
                  >
                    <Typography color="inherit" maxLines={2}>
                      {translations.find(
                        (t) => t.language === ui.selectedLanguage,
                      )?.title || '-'}
                    </Typography>
                  </Stack>

                  <SortableList
                    key={JSON.stringify(
                      questionFields.map((field) => field.id),
                    )}
                    disabled={!canEdit}
                    onPositionChange={(source, destination) => {
                      moveQuestions(source, destination);
                      setValue('ui.selectedQuestionIndex', destination);
                      resetConditionalJumps();
                    }}
                    dragTooltip={
                      hasConditionalJumpRules
                        ? t('surveys.conditional_jump.reorder_warning')
                        : undefined
                    }
                    items={questionFields.map((field, i) => ({
                      id: field.id,
                      content: (
                        <Stack
                          direction="row"
                          py={1.5}
                          px={0.5}
                          spacing={0.5}
                          sx={{
                            backgroundColor:
                              ui.selectedQuestionIndex === i
                                ? SystemColors.primary.tint
                                : SystemColors.white,
                          }}
                          borderRadius={1}
                          boxShadow={` 0 0 0 1px ${SystemColors.grey[90]}`}
                          onClick={() => {
                            setValue('ui.selectedQuestionIndex', i);
                          }}
                          key={field.id}
                        >
                          <SortableList.DragIndicator height={20} />

                          <QuestionsList
                            questionIndex={i}
                            control={control}
                            getValues={getValues}
                          />
                        </Stack>
                      ),
                    }))}
                  />

                  <Box>
                    <Button
                      label={t('surveys.actions.add_page')}
                      variant="tertiary"
                      icon={Add}
                      disabled={!canEdit}
                      onClick={() => {
                        appendQuestion({
                          ...emptyQuestion,
                          answers: [
                            {
                              ...emptyQuestion.answers[0],
                              translations: languages.map((language) => ({
                                ...emptyQuestion.answers[0].translations[0],
                                language,
                              })),
                            },
                          ],
                          translations: languages.map((language) => ({
                            ...emptyQuestion.translations[0],
                            language,
                          })),
                        });
                        setValue(
                          'ui.selectedQuestionIndex',
                          questionFields.length,
                        );
                      }}
                    />
                  </Box>
                </Stack>
              </Paper>
            </Box>

            <Stack sx={{ flex: 1, minWidth: 0 }}>
              <Box
                sx={{
                  display: Number.isNaN(ui.selectedQuestionIndex)
                    ? 'flex'
                    : 'none',
                  flexDirection: 'row',
                  gap: 1,
                  minWidth: 0,
                }}
              >
                <Stack sx={{ flex: 1, minWidth: 0 }} gap={1}>
                  <Paper variant="outlined" sx={{ p: 2, minWidth: 0 }}>
                    <Stack gap={2}>
                      <Typography variant="h3">
                        {t('surveys.content.page_content')}
                      </Typography>

                      {translationsFields.map((field, i) => (
                        <Box
                          key={field.id}
                          sx={{
                            display:
                              ui.selectedLanguage === field.language
                                ? 'block'
                                : 'none',
                            minWidth: 0,
                          }}
                        >
                          <Stack gap={2}>
                            <Controller
                              name={`translations.${i}.title`}
                              control={control}
                              rules={{
                                required: {
                                  value: true,
                                  message: t('form.required'),
                                },
                              }}
                              render={({
                                field: { name, value, onChange, ref },
                                fieldState,
                              }) => {
                                const defaultTranslation =
                                  getValues('translations').find(
                                    (t) => t.language === defaultLanguage,
                                  )?.title ?? '';

                                return (
                                  <TextInput
                                    label={t('common.name')}
                                    name={name}
                                    value={value}
                                    onChange={onChange}
                                    errorMessage={fieldState.error?.message}
                                    infoMessage={
                                      defaultLanguage === field.language
                                        ? undefined
                                        : `${t(
                                            'wallet.employeeWalletFundsSource',
                                          )} (${defaultLanguage}): ${defaultTranslation}`
                                    }
                                    emoji
                                    ai={
                                      isFeatureEnabled('ai')
                                        ? {
                                            inputLanguage: defaultLanguage,
                                            outputLanguage: field.language,
                                            text: defaultTranslation,
                                          }
                                        : undefined
                                    }
                                    disabled={!canEdit}
                                    ref={ref}
                                  />
                                );
                              }}
                            />

                            {settings.withDescription ? (
                              <Controller
                                name={`translations.${i}.description`}
                                control={control}
                                render={({
                                  field: { name, value, onChange, ref },
                                }) => {
                                  const defaultTranslation =
                                    getValues('translations').find(
                                      (t) => t.language === defaultLanguage,
                                    )?.description ?? '';

                                  return (
                                    <TextInput
                                      label={t('employees.details')}
                                      name={name}
                                      value={value}
                                      onChange={onChange}
                                      infoMessage={
                                        defaultLanguage === field.language
                                          ? undefined
                                          : `${t(
                                              'wallet.employeeWalletFundsSource',
                                            )} (${defaultLanguage}): ${defaultTranslation}`
                                      }
                                      emoji
                                      rows={3}
                                      ai={
                                        isFeatureEnabled('ai')
                                          ? {
                                              inputLanguage: defaultLanguage,
                                              outputLanguage: field.language,
                                              text: defaultTranslation,
                                            }
                                          : undefined
                                      }
                                      disabled={!canEdit}
                                      ref={ref}
                                    />
                                  );
                                }}
                              />
                            ) : null}

                            {settings.withImage ? (
                              <Controller
                                name={`translations.${i}.imageId`}
                                control={control}
                                render={({
                                  field: { name, value, onChange, ref },
                                }) => {
                                  return value ? (
                                    <Box>
                                      <Typography color="textSecondary">
                                        {t('form.image')}
                                      </Typography>
                                      <ImageList
                                        items={[
                                          {
                                            imageId: value,
                                            actions: [
                                              {
                                                icon: Delete,
                                                variant: 'neutral-outline',
                                                disabled: !canEdit,
                                                onClick: () => {
                                                  ui.isSameImage
                                                    ? replaceTranslations(
                                                        getValues(
                                                          'translations',
                                                        ).map((field) => ({
                                                          ...field,
                                                          imageId: '',
                                                        })),
                                                      )
                                                    : onChange('');
                                                },
                                              },
                                            ],
                                          },
                                        ]}
                                      />
                                    </Box>
                                  ) : (
                                    <FileInput
                                      name={name}
                                      label={t('form.image')}
                                      onChange={([file]) => {
                                        if (file) {
                                          uploadFile(
                                            { file },
                                            {
                                              onSuccess: ({ data }) => {
                                                ui.isSameImage
                                                  ? replaceTranslations(
                                                      getValues(
                                                        'translations',
                                                      ).map((field) => ({
                                                        ...field,
                                                        imageId: data.id,
                                                      })),
                                                    )
                                                  : onChange(data.id);
                                              },
                                            },
                                          );
                                        } else {
                                          onChange('');
                                        }
                                      }}
                                      disabled={isUploading || !canEdit}
                                      maxFiles={1}
                                      previewSelectedFiles={false}
                                      accept={[
                                        'jpg',
                                        'jpeg',
                                        'png',
                                        'gif',
                                        'webp',
                                      ]}
                                      ai={
                                        isFeatureEnabled('ai')
                                          ? {
                                              title: translations.find(
                                                (t) =>
                                                  t.language ===
                                                  defaultLanguage,
                                              )?.title,
                                              description: translations.find(
                                                (t) =>
                                                  t.language ===
                                                  defaultLanguage,
                                              )?.description,
                                              aspectRatio: [16, 9],
                                            }
                                          : undefined
                                      }
                                      ref={ref}
                                    />
                                  );
                                }}
                              />
                            ) : null}

                            {survey.type === 'training' &&
                            settings.withVideo ? (
                              <Controller
                                name={`translations.${i}.videos`}
                                control={control}
                                render={({ field: { value, onChange } }) => (
                                  <VideoInput
                                    label={t('form.video_from_link')}
                                    value={value}
                                    onChange={onChange}
                                    disabled={!canEdit}
                                    maxVideos={1}
                                  />
                                )}
                              />
                            ) : null}

                            {settings.withAttachments ? (
                              <AttachmentsList
                                control={control}
                                translationIndex={i}
                                disabled={!canEdit}
                              />
                            ) : null}
                          </Stack>
                        </Box>
                      ))}
                    </Stack>
                  </Paper>
                </Stack>

                <Box sx={{ width: 250, flexShrink: 0 }}>
                  <Paper variant="outlined" sx={{ p: 2 }}>
                    <Stack gap={2}>
                      <Typography variant="h3">
                        {t('surveys.content.page_settings')}
                      </Typography>

                      <Controller
                        name="settings.withDescription"
                        control={control}
                        render={({ field: { name, value, onChange, ref } }) => (
                          <SwitchInput
                            label={t('employees.details')}
                            name={name}
                            value={value}
                            onChange={onChange}
                            disabled={!canEdit}
                            ref={ref}
                          />
                        )}
                      />

                      <Controller
                        name="settings.withImage"
                        control={control}
                        render={({ field: { name, value, onChange, ref } }) => (
                          <SwitchInput
                            label={t('form.image')}
                            name={name}
                            value={value}
                            onChange={onChange}
                            disabled={!canEdit}
                            ref={ref}
                          />
                        )}
                      />

                      {survey.type === 'training' ? (
                        <Controller
                          name="settings.withVideo"
                          control={control}
                          render={({
                            field: { name, value, onChange, ref },
                          }) => (
                            <SwitchInput
                              label={t('form.video_from_link')}
                              name={name}
                              value={value}
                              onChange={onChange}
                              disabled={!canEdit}
                              ref={ref}
                            />
                          )}
                        />
                      ) : null}

                      <Controller
                        name="settings.withAttachments"
                        control={control}
                        render={({ field: { name, value, onChange, ref } }) => (
                          <SwitchInput
                            label={t('form.attachments')}
                            name={name}
                            value={value}
                            onChange={onChange}
                            disabled={!canEdit}
                            ref={ref}
                          />
                        )}
                      />

                      {settings.withImage ? (
                        <Controller
                          name="ui.isSameImage"
                          control={control}
                          render={({
                            field: { name, value, onChange, ref },
                          }) => (
                            <SwitchInput
                              label={t('images.same_language.label')}
                              name={name}
                              value={value}
                              onChange={(value) => {
                                onChange(value);

                                if (value === true) {
                                  const defaultImageId =
                                    getValues('translations').find(
                                      (t) => t.language === defaultLanguage,
                                    )?.imageId ?? '';

                                  replaceTranslations(
                                    getValues('translations').map((field) => ({
                                      ...field,
                                      imageId: defaultImageId,
                                    })),
                                  );
                                }
                              }}
                              disabled={!canEdit}
                              ref={ref}
                            />
                          )}
                        />
                      ) : null}
                    </Stack>
                  </Paper>
                </Box>
              </Box>

              {questionFields.map((field, i) => (
                <Box
                  key={field.id}
                  sx={{
                    display: ui.selectedQuestionIndex === i ? 'flex' : 'none',
                    flexDirection: 'row',
                    gap: 1,
                    minWidth: 0,
                  }}
                >
                  <Stack sx={{ flex: 1, minWidth: 0 }} gap={1}>
                    <Paper variant="outlined" sx={{ p: 2, minWidth: 0 }}>
                      <Stack flex={1} gap={2} sx={{ minWidth: 0 }}>
                        <Stack
                          flexDirection="row"
                          justifyContent="space-between"
                          gap={1}
                        >
                          <Typography variant="h3">
                            {t('surveys.content.page_content')}
                          </Typography>

                          <ActionsMenu
                            type="compact"
                            disabled={!canEdit}
                            items={[
                              {
                                label: t('table.clear'),
                                icon: Clear,
                                onClick: () =>
                                  updateQuestion(i, {
                                    ...emptyQuestion,
                                  }),
                              },
                              {
                                label: t('actions.make_copy'),
                                icon: ContentCopy,
                                onClick: () => {
                                  appendQuestion(getValues(`questions.${i}`));
                                  setValue(
                                    'ui.selectedQuestionIndex',
                                    questionFields.length,
                                  );
                                },
                              },
                              null,
                              {
                                label: t('common.remove'),
                                icon: Delete,
                                color: 'error',
                                onClick: () => {
                                  setValue(
                                    'ui.selectedQuestionIndex',
                                    INITIAL_VALUES.ui.selectedQuestionIndex,
                                  );
                                  removeQuestion(i);
                                },
                                disabled:
                                  survey.type !== 'training' &&
                                  questions.length <= 1,
                              },
                            ]}
                          />
                        </Stack>

                        <QuestionTranslationsList
                          questionIndex={i}
                          control={control}
                          getValues={getValues}
                          disabled={!canEdit}
                        />

                        {includes(
                          surveyChoiceAnswerTypes,
                          questions[i].type,
                        ) ? (
                          <AnswersList
                            control={control}
                            languages={languages}
                            questionIndex={i}
                            getValues={getValues}
                            setValue={setValue}
                            trigger={trigger}
                            disabled={!canEdit}
                            dragTooltip={
                              hasConditionalJumpRules
                                ? t('surveys.conditional_jump.reorder_warning')
                                : undefined
                            }
                            onPositionChangeCallback={resetConditionalJumps}
                            surveyType={survey.type}
                          />
                        ) : null}
                      </Stack>
                    </Paper>

                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      gap={1}
                    >
                      <Button
                        label={t('common.previous')}
                        variant="neutral-outline"
                        icon={ArrowBack}
                        onClick={() => {
                          setValue('ui.selectedQuestionIndex', i - 1);
                        }}
                        disabled={i === 0}
                      />
                      <Button
                        label={t('common.next')}
                        variant="neutral-outline"
                        icon={ArrowForward}
                        iconPosition="right"
                        onClick={() => {
                          setValue('ui.selectedQuestionIndex', i + 1);
                        }}
                        disabled={i >= questionFields.length - 1}
                      />
                    </Stack>
                  </Stack>

                  <Box sx={{ width: 250, flexShrink: 0 }}>
                    <Paper variant="outlined" sx={{ p: 2 }}>
                      <Stack gap={2}>
                        <Typography variant="h3">
                          {t('surveys.content.page_settings')}
                        </Typography>

                        <Controller
                          name={`questions.${i}.type`}
                          control={control}
                          render={({
                            field: { name, value, onChange, ref },
                          }) => (
                            <SelectInput
                              label={t('surveys.answerType')}
                              name={name}
                              value={value}
                              onChange={(type) => {
                                if (
                                  !includes(surveyChoiceAnswerTypes, type) ||
                                  !includes(surveyChoiceAnswerTypes, value)
                                ) {
                                  setValue(
                                    `questions.${i}.answers`,
                                    !includes(surveyChoiceAnswerTypes, type)
                                      ? []
                                      : [
                                          {
                                            ...emptyQuestion.answers[0],
                                            translations: languages.map(
                                              (language) => ({
                                                ...emptyQuestion.answers[0]
                                                  .translations[0],
                                                language,
                                              }),
                                            ),
                                          },
                                        ],
                                  );
                                }

                                onChange(type);
                                setValue(
                                  `questions.${i}.answers`,
                                  getValues(`questions.${i}.answers`).map(
                                    (answer) => ({
                                      ...answer,
                                      isCorrect:
                                        emptyQuestion.answers[0].isCorrect,
                                    }),
                                  ),
                                );
                              }}
                              options={(survey.type === 'training'
                                ? [
                                    ...surveyChoiceAnswerTypes,
                                    'information_page',
                                  ]
                                : [
                                    ...surveyAnswerTypes.filter(
                                      (type) =>
                                        !includes(
                                          [
                                            ...surveyRatingScaleAnswerTypes,
                                            'information_page',
                                          ],
                                          type,
                                        ),
                                    ),
                                    'rating_scale',
                                  ]
                              ).map((type) => ({
                                label: t(`surveys.answerType-${type}`),
                                value: type,
                              }))}
                              disabled={!canEdit}
                              ref={ref}
                            />
                          )}
                        />

                        {questions[i].type === 'rating_scale' ? (
                          <Controller
                            name={`questions.${i}.subType`}
                            control={control}
                            rules={{
                              required: {
                                value: true,
                                message: t('form.required'),
                              },
                            }}
                            render={({
                              field: { name, value, onChange, ref },
                              fieldState,
                            }) => (
                              <SelectInput
                                label={t('surveys.answerType-rating_scale')}
                                name={name}
                                value={value}
                                onChange={onChange}
                                error={fieldState.error?.message}
                                options={surveyRatingScaleAnswerTypes.map(
                                  (type) => ({
                                    label: t(`surveys.answerType-${type}`),
                                    value: type,
                                  }),
                                )}
                                disabled={!canEdit}
                                ref={ref}
                              />
                            )}
                          />
                        ) : null}

                        {includes(
                          surveyChoiceAnswerTypes,
                          questions[i].type,
                        ) ? (
                          <Controller
                            name={`questions.${i}.choiceAnswerFormat`}
                            control={control}
                            rules={{
                              required: {
                                value: true,
                                message: t('form.required'),
                              },
                            }}
                            render={({
                              field: { name, value, onChange, ref },
                              fieldState,
                            }) => (
                              <SelectInput
                                label={t('surveys.answerFormat.label')}
                                name={name}
                                value={value}
                                onChange={onChange}
                                error={fieldState.error?.message}
                                options={surveyChoiceAnswerFormats.map(
                                  (format) => ({
                                    label: t(`surveys.answerFormat.${format}`),
                                    value: format,
                                  }),
                                )}
                                disabled={!canEdit}
                                ref={ref}
                              />
                            )}
                          />
                        ) : null}

                        <Controller
                          name={`questions.${i}.withImage`}
                          control={control}
                          render={({
                            field: { name, value, onChange, ref },
                          }) => (
                            <SwitchInput
                              label={t('form.image')}
                              name={name}
                              value={value}
                              onChange={onChange}
                              disabled={!canEdit}
                              ref={ref}
                            />
                          )}
                        />

                        <Controller
                          name={`questions.${i}.withDescription`}
                          control={control}
                          render={({
                            field: { name, value, onChange, ref },
                          }) => (
                            <SwitchInput
                              label={t('employees.details')}
                              name={name}
                              value={value}
                              onChange={onChange}
                              disabled={!canEdit}
                              ref={ref}
                            />
                          )}
                        />

                        {survey.type === 'training' &&
                        questions[i].type === 'information_page' ? (
                          <Controller
                            name={`questions.${i}.withVideo`}
                            control={control}
                            render={({
                              field: { name, value, onChange, ref },
                            }) => (
                              <SwitchInput
                                label={t('form.video_from_link')}
                                name={name}
                                value={value}
                                onChange={onChange}
                                disabled={!canEdit}
                                ref={ref}
                              />
                            )}
                          />
                        ) : null}

                        {questions[i].type === 'information_page' ? (
                          <Controller
                            name={`questions.${i}.withAttachments`}
                            control={control}
                            render={({
                              field: { name, value, onChange, ref },
                            }) => (
                              <SwitchInput
                                label={t('form.attachments')}
                                name={name}
                                value={value}
                                onChange={onChange}
                                disabled={!canEdit}
                                ref={ref}
                              />
                            )}
                          />
                        ) : null}

                        {survey.type === 'training' ? null : (
                          <Controller
                            name={`questions.${i}.isRequired`}
                            control={control}
                            render={({
                              field: { name, value, onChange, ref },
                            }) => (
                              <SwitchInput
                                label={t('surveys.question_required')}
                                name={name}
                                value={value}
                                onChange={onChange}
                                disabled={!canEdit}
                                ref={ref}
                              />
                            )}
                          />
                        )}

                        {survey.type === 'training'
                          ? null
                          : (() => {
                              const conditionalJumpOptions = questions
                                .map((q, qi) => {
                                  return {
                                    label: `Q${qi + 1}: ${
                                      q.translations.find(
                                        (t) => t.language === defaultLanguage,
                                      )?.text ?? ''
                                    }`,
                                    value: qi,
                                  };
                                })
                                .filter((q) => q.value > i + 1);

                              return conditionalJumpOptions.length &&
                                questions[i].type ===
                                  'single_choice_response' ? (
                                <Controller
                                  name={`questions.${i}.conditionalJump.isEnabled`}
                                  control={control}
                                  render={({
                                    field: { name, value, onChange, ref },
                                  }) => {
                                    return (
                                      <>
                                        <SwitchInput
                                          label={t(
                                            'surveys.conditional_jump.label',
                                          )}
                                          info={t(
                                            'surveys.conditional_jump.info',
                                          )}
                                          name={name}
                                          value={value}
                                          onChange={onChange}
                                          disabled={!canEdit}
                                          ref={ref}
                                        />

                                        <Collapse in={value}>
                                          <Stack gap={1}>
                                            {questions[i].answers.map(
                                              (answer, ai) => {
                                                return (
                                                  <SelectInput
                                                    label={`${
                                                      answer.translations.find(
                                                        (t) =>
                                                          t.language ===
                                                          defaultLanguage,
                                                      )?.text
                                                    } →`}
                                                    value={
                                                      questions[
                                                        i
                                                      ].conditionalJump.rules.find(
                                                        (r) =>
                                                          r.answerIndex === ai,
                                                      )?.questionIndex ?? -1
                                                    }
                                                    onChange={(v) => {
                                                      setValue(
                                                        `questions.${i}.conditionalJump.rules`,
                                                        [
                                                          ...questions[
                                                            i
                                                          ].conditionalJump.rules.filter(
                                                            (r) =>
                                                              r.answerIndex !==
                                                              ai,
                                                          ),
                                                          ...(v === -1
                                                            ? []
                                                            : [
                                                                {
                                                                  answerIndex:
                                                                    ai,
                                                                  questionIndex:
                                                                    v,
                                                                },
                                                              ]),
                                                        ],
                                                      );
                                                    }}
                                                    options={[
                                                      {
                                                        label: t('common.next'),
                                                        value: -1,
                                                      },
                                                      ...conditionalJumpOptions,
                                                    ]}
                                                    disabled={!canEdit}
                                                    key={ai}
                                                  />
                                                );
                                              },
                                            )}
                                          </Stack>
                                        </Collapse>
                                      </>
                                    );
                                  }}
                                />
                              ) : null;
                            })()}
                      </Stack>
                    </Paper>
                  </Box>
                </Box>
              ))}
            </Stack>
          </Box>
        </Stack>

        <Footer visible={isDirty || survey?.status === 'draft'}>
          <Stack direction="row" justifyContent="end" gap="10px">
            {isDirty ? (
              <>
                <Button
                  label={t('common.cancel')}
                  variant="neutral-outline"
                  onClick={() => {
                    reset(undefined, { keepDirty: false });
                  }}
                />
                <Button
                  label={t('common.save')}
                  variant="secondary"
                  type="submit"
                  disabled={isUpdating || !hasSurveyEditPermission}
                />
              </>
            ) : null}
            {survey?.status === 'draft' &&
            survey.publishDateTime &&
            !isDirty ? (
              <Button
                label={t('surveys.activate', {
                  startDate: moment(survey.publishDateTime).format('l'),
                  interpolation: { escapeValue: false },
                })}
                variant="primary"
                onClick={() => {
                  activateSurvey(
                    { surveyId },
                    {
                      onSuccess: () => {
                        predefinedToasts.success.updated();
                      },
                    },
                  );
                }}
                disabled={isActivating || !hasSurveyEditPermission}
              />
            ) : null}
          </Stack>
        </Footer>
      </form>

      <DataLossPrompt when={!hasSurveyEditPermission ? false : isDirty} />
    </>
  );
};
